import React, { useCallback, useEffect } from 'react';
import Button from '@atlaskit/button/standard-button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalTransition,
	ModalBody,
	ModalTitle,
	ModalHeader,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { Intl, ProjectKey, BaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
import { useMetricActions } from '../../../../controllers/metric-actions';
import { useMetricResource } from '../../../../controllers/metric-resource/index.tsx';
import { useFetchReportsService } from '../../../../services/impacted-reports/index.tsx';
import { useRemoveMetricService } from '../../../../services/remove-metric/index.tsx';
import { Flags } from './flags';
import messages from './messages';
import { ReportListMessage } from './report-list';

type Props = {
	isOpen: boolean;
	baseUrl: BaseUrl;
	projectKey: ProjectKey;
	metricName: string;
	metricId: string;
} & Intl & {
		onClose: () => void;
	};

export const DeleteMetricModalComponent = ({
	baseUrl,
	projectKey,
	isOpen,
	onClose,
	metricName,
	metricId,
	intl: { formatMessage },
}: Props) => {
	const [, { replacePath }] = useMetricActions();

	const [, { refresh: onResourceRefresh }] = useMetricResource();

	const [{ loading: loadingFetchReports, error, data: reports }, { fetch: fetchReports }] =
		useFetchReportsService({
			baseUrl,
			projectKey,
		});

	const [
		{ loading: loadingRemoveMetric, error: errorRemoveMetric, data: dataRemoveMetric },
		{ fetch: requestRemoveMetric },
	] = useRemoveMetricService({
		baseUrl,
		projectKey,
	});

	const handleDeleteMetricClick = useCallback(() => {
		requestRemoveMetric(metricId);
	}, [requestRemoveMetric, metricId]);

	// get list of reports that using this metric
	useEffect(() => {
		const asyncFetchReports = async () => {
			await fetchReports(metricId);
		};

		if (isOpen) {
			asyncFetchReports();
		}
	}, [fetchReports, metricId, isOpen]);

	// update browser history after deleting the metric
	useEffect(() => {
		if (dataRemoveMetric === true) {
			replacePath();
			onResourceRefresh();
		}
	}, [dataRemoveMetric, onResourceRefresh, replacePath]);

	useEffect(() => {
		if (error || errorRemoveMetric) {
			onClose();
		}
	}, [error, errorRemoveMetric, onClose]);

	return (
		<ModalTransition>
			{isOpen && (
				<>
					<ShortcutScope>
						<ModalDialog
							onClose={onClose}
							// @ts-expect-error TS2769 | TS2769 - No overload matches this call. | No overload matches this call.
							loading={loadingFetchReports || loadingRemoveMetric}
						>
							<ModalHeader>
								<ModalTitle>{formatMessage(messages.deleteMetricModalHeader)}</ModalTitle>
							</ModalHeader>
							<ModalBody>
								<p>{formatMessage(messages.deleteMetricModalBody)}</p>
								{loadingFetchReports === false && (
									<ReportListMessage
										projectKey={projectKey}
										metricName={metricName}
										reports={reports}
									/>
								)}
							</ModalBody>
							<ModalFooter>
								<Button
									onClick={handleDeleteMetricClick}
									appearance="primary"
									testId="servicedesk-sla-settings.ui.metrics.summary-with-actions.delete-metric-modal.delete-metric-button"
								>
									{formatMessage(messages.deleteButton)}
								</Button>
								<Button onClick={onClose} appearance="subtle">
									{formatMessage(messages.cancelButton)}
								</Button>
							</ModalFooter>
						</ModalDialog>
					</ShortcutScope>
				</>
			)}
			<Flags error={errorRemoveMetric} metricName={metricName} />
		</ModalTransition>
	);
};

export const DeleteMetricModal = injectIntl(DeleteMetricModalComponent);
